import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useRef } from 'react';
import type { DailyEnergy } from '../../api/wattelEM';
import { useI18n } from '../../i18n';

interface EnergyOverTimeChartProps {
  data: DailyEnergy[];
  loading?: boolean;
  title?: string;
  hourly?: boolean;
  rangeDays?: number;       // e.g. 7, 30, 90 (used when no explicit start/end)
  rangeStartDate?: string;  // YYYY-MM-DD (custom range)
  rangeEndDate?: string;    // YYYY-MM-DD (custom range)
}

function EnergyOverTimeChart({ data, loading, title, hourly = false, rangeDays, rangeStartDate, rangeEndDate }: EnergyOverTimeChartProps) {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const { t } = useI18n();
  const chartTitle = title ?? t('eq2_eot_title');
  const energyName = t('eq2_eot_energy');
  const avgPowerName = t('eq2_eot_avg_power');
  const co2Name = t('eq2_eot_co2');

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-6 h-80 flex items-center justify-center">
        <div className="w-12 h-12 relative">
          <div className="absolute inset-0 rounded-full border-4 border-gray-100"></div>
          <div className="absolute inset-0 rounded-full border-4 border-[#1e88e5] border-t-transparent animate-spin"></div>
        </div>
      </div>
    );
  }

  if (data.length === 0 && !rangeDays && !rangeStartDate) {
    return (
      <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-6 h-80 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-[#6b7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-sm font-semibold text-[#0d3b66]">{t('eq2_eot_no_data')}</p>
          <p className="text-xs mt-1 text-[#6b7280]">{t('eq2_eot_no_data_hint')}</p>
        </div>
      </div>
    );
  }

  // Parse timestamps: handles ISO "2026-04-20T00:00:00.000Z", MySQL "2026-04-20 14:00:00", or date-only "2026-04-20"
  const parseTs = (s: string) => {
    // Already a full ISO string with Z — parse directly
    if (s.endsWith('Z') || s.includes('+')) return new Date(s).getTime();
    // MySQL datetime with space — treat as UTC
    if (s.includes(' ')) return new Date(s.replace(' ', 'T') + 'Z').getTime();
    // Has T but no Z — treat as UTC
    if (s.includes('T')) return new Date(s + 'Z').getTime();
    // Date-only — local midnight
    return new Date(s + 'T00:00:00').getTime();
  };

  // For hourly mode: fill all 24 hours so gaps show as 0
  let filledData = data;
  if (hourly) {
    // Use UTC to match server timestamps
    const nowUtc = Math.floor(Date.now() / 3600000) * 3600000; // current hour in UTC ms
    const existingMap = new Map(data.map(d => {
      const ts = parseTs(d.day);
      // Round to hour
      const key = Math.floor(ts / 3600000) * 3600000;
      return [key, d];
    }));
    const filled: typeof data = [];
    for (let i = 23; i >= 0; i--) {
      const hourMs = nowUtc - i * 3600000;
      const existing = existingMap.get(hourMs);
      if (existing) {
        filled.push(existing);
      } else {
        const d = new Date(hourMs);
        const iso = d.toISOString().slice(0, 13) + ':00:00';
        filled.push({ day: iso, daily_kwh: 0, avg_power_kw: 0, max_power_kw: 0 });
      }
    }
    filledData = filled;
  }

  // For daily mode: fill all days in the selected range so missing days appear as 0 (not skipped)
  if (!hourly && (rangeDays || rangeStartDate)) {
    const DAY_MS = 86_400_000;
    const toLocalMidnight = (ts: number) => {
      const d = new Date(ts);
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    };
    const existingMap = new Map(data.map(d => {
      const ts = parseTs(d.day);
      return [toLocalMidnight(ts), d];
    }));
    // Determine start & end
    let startMs: number;
    let endMs: number;
    if (rangeStartDate && rangeEndDate) {
      startMs = toLocalMidnight(new Date(rangeStartDate + 'T00:00:00').getTime());
      endMs = toLocalMidnight(new Date(rangeEndDate + 'T00:00:00').getTime());
    } else {
      endMs = toLocalMidnight(Date.now());
      startMs = endMs - (Math.max(1, rangeDays || 30) - 1) * DAY_MS;
    }
    const filled: typeof data = [];
    for (let t = startMs; t <= endMs; t += DAY_MS) {
      const existing = existingMap.get(t);
      if (existing) {
        filled.push(existing);
      } else {
        const iso = new Date(t).toISOString().slice(0, 10);
        filled.push({ day: iso, daily_kwh: 0, avg_power_kw: 0, max_power_kw: 0 });
      }
    }
    filledData = filled;
  }

  // Cumulative energy consumed over the selected period (running sum of per-bucket kWh)
  let cumulativeKwh = 0;
  const energyData = filledData.map(d => {
    cumulativeKwh += Number(d.daily_kwh) || 0;
    return {
      x: parseTs(d.day),
      y: cumulativeKwh,
    };
  });

  const avgPowerData = filledData.map(d => ({
    x: parseTs(d.day),
    y: (Number(d.avg_power_kw) || 0) * 1000,
  }));

  // CO₂ cumulé : énergie cumulée × facteur d'émission France (0.052 kg CO₂/kWh - source ADEME)
  const CO2_FACTOR = 0.052;
  let cumulativeCo2 = 0;
  const co2Data = filledData.map(d => {
    cumulativeCo2 += (Number(d.daily_kwh) || 0) * CO2_FACTOR;
    return {
      x: parseTs(d.day),
      y: cumulativeCo2,
    };
  });

  // ZCE Brand Colors
  const zceNavy = '#0d3b66';
  const zceBlue = '#1e88e5';
  const zceOrange = '#fca311';
  const zceGreen = '#16a34a';
  const zceGrayMuted = '#6b7280';

  const options: Highcharts.Options = {
    time: { useUTC: false } as Highcharts.TimeOptions,
    chart: {
      height: 350,
      style: { fontFamily: 'Inter, Montserrat, system-ui, sans-serif' },
      zooming: { type: 'x' },
      backgroundColor: 'transparent',
    },
    title: {
      text: chartTitle,
      style: { fontSize: '16px', fontWeight: '600', color: zceNavy },
    },
    subtitle: {
      text: t('eq2_eot_subtitle'),
      style: { fontSize: '11px', color: zceGrayMuted },
    },
    xAxis: {
      type: 'datetime',
      labels: {
        style: { fontSize: '11px', color: zceGrayMuted },
        format: hourly ? '{value:%H:%M}' : undefined,
      },
      lineColor: '#e5e7eb',
      gridLineColor: '#f5f5f5',
    },
    yAxis: [
      {
        title: { text: energyName, style: { color: zceBlue, fontWeight: '600' } },
        labels: { format: '{value}', style: { fontSize: '11px', color: zceBlue } },
        gridLineColor: '#f5f5f5',
        min: 0,
      },
      {
        title: { text: avgPowerName, style: { color: zceOrange, fontWeight: '600' } },
        labels: { format: '{value}', style: { fontSize: '11px', color: zceOrange } },
        opposite: true,
        gridLineWidth: 0,
        min: 0,
      },
      {
        title: { text: 'CO₂ (kg)', style: { color: zceGreen, fontWeight: '600' } },
        labels: { format: '{value:.3f}', style: { fontSize: '11px', color: zceGreen } },
        opposite: true,
        gridLineWidth: 0,
        min: 0,
        visible: true,
      },
    ],
    tooltip: {
      shared: true,
      xDateFormat: hourly ? '%d/%m/%Y %H:%M' : '%d/%m/%Y',
      backgroundColor: 'white',
      borderColor: '#e5e7eb',
      borderRadius: 8,
      shadow: { color: 'rgba(13, 59, 102, 0.1)', offsetX: 0, offsetY: 2, width: 8 },
      headerFormat: '<span style="font-size: 12px; font-weight: 600; color: ' + zceNavy + ';">{point.key}</span><br/>',
      pointFormatter: function(this: Highcharts.Point) {
        const series = this.series;
        if (series.name === co2Name) {
          return `<span style="color:${series.color}">●</span> ${series.name}: <b>${(this.y ?? 0).toFixed(3)} kg</b><br/>`;
        }
        if (series.name === energyName) {
          return `<span style="color:${series.color}">●</span> ${series.name}: <b>${(this.y ?? 0).toFixed(3)} kWh</b><br/>`;
        }
        return `<span style="color:${series.color}">●</span> ${series.name}: <b>${(this.y ?? 0).toFixed(1)}</b><br/>`;
      },
    },
    legend: {
      enabled: true,
      align: 'center',
      verticalAlign: 'bottom',
      itemStyle: { fontSize: '12px', color: zceNavy, fontWeight: '500' },
    },
    plotOptions: {
      areaspline: {
        fillOpacity: 0.3,
        marker: { enabled: false, states: { hover: { enabled: true, fillColor: 'white', lineColor: zceBlue, lineWidth: 2 } } },
        lineWidth: 2.5,
      },
      spline: {
        marker: { enabled: false, states: { hover: { enabled: true, fillColor: 'white', lineColor: zceOrange, lineWidth: 2 } } },
        lineWidth: 2.5,
      },
    },
    series: [
      {
        name: energyName,
        type: 'areaspline',
        yAxis: 0,
        data: energyData,
        color: zceBlue,
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [[0, 'rgba(30, 136, 229, 0.3)'], [1, 'rgba(30, 136, 229, 0.02)']],
        },
      },
      {
        name: avgPowerName,
        type: 'spline',
        yAxis: 1,
        data: avgPowerData,
        color: zceOrange,
        dashStyle: 'ShortDash',
      },
      {
        name: co2Name,
        type: 'spline',
        yAxis: 2,
        data: co2Data,
        color: zceGreen,
        dashStyle: 'ShortDot',
        lineWidth: 2,
        marker: { enabled: false, states: { hover: { enabled: true, fillColor: 'white', lineColor: zceGreen, lineWidth: 2 } } },
      },
    ],
    credits: { enabled: false },
  };

  return (
    <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-5">
      <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
    </div>
  );
}

export default EnergyOverTimeChart;
